import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import SectionEditForm from './SectionEditForm';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';

const FIELDS = [
  { key: 'phone', label: 'Phone', type: 'tel', placeholder: '503-555-0100' },
  { key: 'website', label: 'Website', type: 'url', placeholder: 'https://' },
  { key: 'email', label: 'Email', type: 'email', placeholder: 'hello@' },
];

const SOCIALS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'twitter', label: 'X / Twitter' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'youtube', label: 'YouTube' },
];

/**
 * Phone, website, email and social handles — three of the nine checklist items.
 * PATCHes `contact` only, so nothing else on the listing can be disturbed by
 * someone checking their phone number.
 */
export default function ContactEditForm() {
  const { lid } = useParams();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const currentUser = useSelector((s) => s.auth.user);
  const role = currentUser?.role === 'admin' ? 'admin' : 'industry';
  const [contact, setContact] = useState({ ...(listing?.contact || {}) });

  // Reachable straight from the checklist, so the listing detail page — which is
  // what normally fills redux — may never have mounted.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (listing?.id === lid) return;
      try {
        const loaded = await client.service(`${role}/listing`).get(lid);
        if (cancelled) return;
        dispatch(setListing(loaded));
        setContact({ ...(loaded.contact || {}) });
      } catch (e) {
        console.log('could not load listing', e);
      }
    })();
    return () => { cancelled = true; };
  }, [lid, listing?.id, dispatch]);

  if (!listing || listing.id !== lid) return null;

  const set = (key, value) => setContact((c) => ({ ...c, [key]: value }));

  const input = (f) => (
    <div key={f.key}>
      <label htmlFor={f.key} className="block text-sm font-medium text-gray-700">
        {f.label}
      </label>
      <input
        id={f.key}
        name={f.key}
        type={f.type || 'text'}
        placeholder={f.placeholder}
        value={contact[f.key] || ''}
        onChange={(e) => set(f.key, e.target.value)}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-800 focus:ring-red-800 sm:text-sm"
      />
    </div>
  );

  return (
    <SectionEditForm
      listing={listing}
      title="Contact"
      description="How visitors get in touch, and where they find you online."
      backTo={`/${role}/listings/${lid}`}
      buildPatch={() => ({ contact })}
      onSaved={(updated) => dispatch(setListing({ ...listing, contact: updated.contact ?? contact }))}
    >
      {FIELDS.map(input)}

      <div className="pt-2">
        <h4 className="text-sm font-medium text-gray-700">Reservations</h4>
        {/* Open field since 2026-09-12: a booking link is free to every
            listing, so it is no longer wrapped in FeatureDisabled. */}
        <div className="mt-2">
          <label htmlFor="reservationUrl" className="block text-sm font-medium text-gray-700">
            Booking link
          </label>
          <input
            id="reservationUrl"
            name="reservationUrl"
            type="url"
            placeholder="https://"
            value={contact.reservationUrl || ''}
            onChange={(e) => set('reservationUrl', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-800 focus:ring-red-800 sm:text-sm"
          />
        </div>
      </div>

      <div className="pt-2">
        <h4 className="text-sm font-medium text-gray-700">Social profiles</h4>
        <div className="mt-2 space-y-3">
          {SOCIALS.map(input)}
        </div>
      </div>
    </SectionEditForm>
  );
}
